import React, { useEffect, useRef } from 'react';
import { CloseButton } from '../base/NavButtons';
import { theme } from '../../theme';
import { TabsKeepLabels, TabsLevel } from './Tabs';
import { FOCUSABLE_SELECTOR, isTopModal, openModalCount, pushOpenModal, removeOpenModal } from './dialogStack';

import { tokenVars } from '../../theme/tokens';
// Feature-detect dynamic viewport units (dvh/dvw); fall back to vh/vw for older browsers
const supportsDvUnits = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('height', '1dvh');
const dvh100 = supportsDvUnits ? '100dvh' : '100vh';
const dvw100 = supportsDvUnits ? '100dvw' : '100vw';
const mobileMaxHeight = supportsDvUnits ? 'calc(100dvh - 80px)' : 'calc(100vh - 80px)';

// Inline scroll management with counter to handle multiple modals
let scrollLockCount = 0;
let originalBodyOverflow = '';
let originalBodyPaddingRight = '';
let originalBodyPosition = '';
let originalBodyTop = '';
let scrollPosition = 0;

const lockBodyScroll = () => {
  if (scrollLockCount === 0) {
    // Save current scroll position
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Save original values
    originalBodyOverflow = document.body.style.overflow;
    originalBodyPaddingRight = document.body.style.paddingRight;
    originalBodyPosition = document.body.style.position;
    originalBodyTop = document.body.style.top;

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Lock scroll with position fixed to prevent any scroll on mobile
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%';

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }
  scrollLockCount++;
};

const unlockBodyScroll = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1);

  if (scrollLockCount === 0) {
    // Restore original values
    document.body.style.overflow = originalBodyOverflow;
    document.body.style.paddingRight = originalBodyPaddingRight;
    document.body.style.position = originalBodyPosition;
    document.body.style.top = originalBodyTop;
    document.body.style.width = '';

    // Restore scroll position
    window.scrollTo(0, scrollPosition);
  }
};

/** How many Modal dialogs are open. Exported for tests. */
export { openModalCount };

export interface ModalProps {
  // Support both APIs for backward compatibility
  isOpen?: boolean;
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
  maxHeight?: string;
  /**
   * Floor for the modal body on tablet/desktop.
   *
   * Without it the modal is content-sized (height is left undefined), so a
   * tabbed dialog grows and shrinks every time the user switches tab —
   * Details is short, a user list is long, and the frame jumps between them.
   * Setting a floor near the height of the COMMON panel keeps the frame still
   * while the two or three genuinely long panels scroll inside it. Sizing to
   * the tallest panel instead would leave every short tab in a field of empty
   * space.
   *
   * Ignored on mobile and in full-screen, where the modal already fills the
   * viewport and there is nothing to stabilise.
   */
  minHeight?: string;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean; // Default: false (not closed by clicking outside modal)
  preventAutoClose?: boolean; // Legacy prop for backward compatibility
  style?: React.CSSProperties;
  fullScreen?: boolean; // When true, modal takes full viewport with no padding/borders
  zIndex?: number; // Custom z-index for nested modals (default: theme.zIndex.modal = 9000)
  stickyFooter?: boolean; // When true, footer stays fixed at bottom (mobile-friendly)
  mobileFullScreen?: boolean; // When true, modal is fullscreen on mobile devices (<768px)
}

export const Modal: React.FC<ModalProps> = ({
  isOpen = true, // If not provided, assume modal is open (for backward compatibility with old API)
  title,
  onClose,
  children,
  width = 'min(1000px, 95vw)',
  maxHeight = '90vh',
  minHeight,
  footer,
  showCloseButton = true,
  closeOnBackdropClick = false, // Default: NOT closed by clicking outside modal
  preventAutoClose = false, // Legacy prop
  style,
  fullScreen = false,
  zIndex = theme.zIndex.modal,
  stickyFooter = false,
  mobileFullScreen = false,
}) => {
  // Check if we're on mobile (client-side only)
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine if fullscreen should be active (explicit fullScreen or mobileFullScreen on mobile)
  const isFullScreenActive = fullScreen || (mobileFullScreen && isMobile);
  const modalRef = useRef<HTMLDivElement>(null);
  const didLockScrollRef = useRef(false); // Track if THIS modal instance locked scroll

  // Combine closeOnBackdropClick logic with preventAutoClose for backward compatibility
  const shouldPreventAutoClose = preventAutoClose || !closeOnBackdropClick;

  // Enhanced close handler
  const handleClose = React.useCallback(() => {
    // Don't unlock here - let useEffect handle it when isOpen becomes false
    onClose();
  }, [onClose]);

  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen && !didLockScrollRef.current) {
      didLockScrollRef.current = true;
      lockBodyScroll();
    } else if (!isOpen && didLockScrollRef.current) {
      didLockScrollRef.current = false;
      unlockBodyScroll();
    }
  }, [isOpen]);

  // Cleanup on unmount - ensure scroll is unlocked if modal unmounts while open
  useEffect(() => {
    return () => {
      if (didLockScrollRef.current) {
        didLockScrollRef.current = false;
        unlockBodyScroll();
      }
    };
  }, []);

  // Place in the stack of open dialogs (see openModalStack). Registered in a
  // layout effect so a dialog opened from inside another one is already on
  // top by the time any key reaches either listener.
  const stackTokenRef = useRef<symbol>(Symbol('modal'));
  // One title id per dialog: with dialogs stacked, a shared id made each
  // dialog's aria-labelledby point at the first title in the page.
  const titleId = `modal-title-${React.useId()}`;
  React.useLayoutEffect(() => {
    if (!isOpen) return;
    const token = stackTokenRef.current;
    pushOpenModal(token);
    return () => removeOpenModal(token);
  }, [isOpen]);

  // Handle ESC key to close modal — only the top dialog closes.
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || !isTopModal(stackTokenRef.current)) return;
      // Claim the key press: closing this dialog can make the one underneath
      // the top of the stack before ITS listener runs for this same event.
      e.preventDefault();
      handleClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Ref for overlay to attach native touch event listener
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus management:
  // 1. When the modal opens, stash whatever element was focused so we can
  //    restore it on close (keeps keyboard nav coherent — the user comes
  //    back to the button that opened the modal).
  // 2. Move focus into the modal. Prefer the first focusable element so
  //    the user can immediately tab through the form rather than having
  //    to first tab past the modal container itself.
  // 3. Trap Tab / Shift+Tab inside the modal while open (WCAG 2.1 2.4.3
  //    Focus Order + 2.4.7 Focus Visible — without this, Tab leaks out
  //    to the (visually hidden but DOM-present) background page).
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Defer the focus move by one frame so any auto-focus from form fields
    // inside the modal's own useEffects has settled.
    const raf = requestAnimationFrame(() => {
      const root = modalRef.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first || root).focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      const prev = previouslyFocusedRef.current;
      // document.activeElement may have moved off the previously-focused
      // element if it was removed from the DOM; guard with isConnected.
      if (prev && prev.isConnected && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isOpen]);

  // Focus trap: intercept Tab / Shift+Tab and cycle within the modal.
  useEffect(() => {
    if (!isOpen) return;
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      // A dialog underneath another one leaves Tab to the top dialog's trap.
      if (!isTopModal(stackTokenRef.current)) return;
      const root = modalRef.current;
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null
      );
      if (focusables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Prevent touch scrolling on the overlay backdrop itself,
  // but allow scrolling inside modal content.
  // We use a native event listener with { passive: false } so we can call
  // preventDefault() on touchmove. This replaces 'touch-action: none' on the
  // overlay, which on iOS Safari blocks ALL descendant scrolling including
  // overflow:auto containers inside the modal.
  useEffect(() => {
    if (!isOpen) return;
    const overlay = overlayRef.current;
    if (!overlay) return;

    const handleTouchMove = (e: TouchEvent) => {
      // Only block touch scrolling that originates directly on the overlay backdrop.
      // If the touch target is inside the modal dialog, allow it to scroll normally.
      if (e.target === overlay) {
        e.preventDefault();
      }
    };

    overlay.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      overlay.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen]);

  // Calculate if we need sticky footer behavior
  const useStickyFooter = stickyFooter || (footer && isMobile);

  if (!isOpen) return null;

  // Prevent all interactions with background
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Don't close modal if shouldPreventAutoClose is enabled
    if (shouldPreventAutoClose) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Don't close modal if there's an active file input dialog
    const activeElement = document.activeElement;
    const hasFileInput = document.querySelector('input[type="file"]');

    // If there's a file input and it might be opening a dialog, don't close
    if (hasFileInput && (activeElement === hasFileInput || (e.target as HTMLElement).closest('[data-modal-content]'))) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Only close on direct overlay clicks, not on modal content clicks
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Prevent scrolling the background when scrolling inside modal
  const handleWheel = (e: React.WheelEvent) => {
    // Only prevent if the scroll is on the overlay (not modal content)
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        inset: 0,
        background: tokenVars.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex,
        pointerEvents: 'auto',
        // NOTE: Do NOT set touchAction: 'none' here. On iOS Safari, touch-action: none
        // on a parent element blocks ALL touch-initiated scrolling on descendants,
        // even those with overflow: auto. Instead, we use a native touchmove event
        // listener (above) that selectively prevents scrolling only on the backdrop itself.
        overscrollBehavior: 'contain', // Prevent scroll chaining on mobile
        padding: isMobile ? '10px 0' : theme.spacing[4],
      }}
      onClick={handleOverlayClick}
      onWheel={handleWheel}
    >
      <div
        ref={modalRef}
        style={{
          background: tokenVars.surface,
          border: isFullScreenActive ? 'none' : `1px solid ${tokenVars.borderSubtle}`,
          borderRadius: isFullScreenActive
            ? 0
            : isMobile
              ? `${theme.borders.modal.radius}px ${theme.borders.modal.radius}px 0 0`
              : theme.borders.modal.radius,
          // A phone-only full screen (mobileFullScreen) keeps a side gutter so
          // text does not touch the screen edge; an explicit fullScreen stays edge to edge.
          padding: useStickyFooter || fullScreen ? 0 : isFullScreenActive ? theme.spacing[4] : theme.spacing.component.modal.padding,
          width: isFullScreenActive ? dvw100 : isMobile ? '100vw' : width,
          height: isFullScreenActive ? dvh100 : isMobile ? 'auto' : undefined,
          // minHeight only where the modal is a floating panel — on mobile and
          // full-screen it already fills the viewport.
          minHeight: isFullScreenActive || isMobile ? undefined : minHeight,
          maxHeight: isFullScreenActive ? dvh100 : isMobile ? mobileMaxHeight : maxHeight,
          maxWidth: isFullScreenActive ? dvw100 : undefined,
          overflowY: useStickyFooter ? 'hidden' : 'auto',
          overflowX: 'hidden',
          position: isFullScreenActive ? 'fixed' : 'relative',
          top: isFullScreenActive ? 0 : undefined,
          left: isFullScreenActive ? 0 : undefined,
          right: isFullScreenActive ? 0 : undefined,
          bottom: isFullScreenActive ? 0 : undefined,
          margin: isFullScreenActive ? 0 : undefined,
          pointerEvents: 'auto',
          boxShadow: isFullScreenActive ? 'none' : theme.shadows.modal.dialog,
          boxSizing: 'border-box',
          display: useStickyFooter ? 'flex' : undefined,
          flexDirection: useStickyFooter ? 'column' : undefined,
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
          overscrollBehavior: 'contain', // Prevent scroll from escaping modal
          ...style,
        }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        data-modal-content="true"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: useStickyFooter ? `${theme.spacing[3]}px ${theme.spacing[4]}px` : undefined,
              marginBottom: useStickyFooter ? 0 : theme.spacing.component.modal.headerGap,
              borderBottom: useStickyFooter ? `1px solid ${tokenVars.borderSubtle}` : undefined,
              flexShrink: 0,
              minHeight: isMobile ? 56 : undefined, // Touch-friendly header height
            }}
          >
            {title && (
              <h2
                id={titleId}
                style={{
                  ...theme.typography.styles.h4,
                  margin: 0,
                  fontSize: isMobile ? 18 : theme.typography.fontSize.lg,
                }}
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              // The shared Close button (outlined, 44px on a phone) so every
              // dialog closes the same, easy-to-spot way.
              <CloseButton onClick={handleClose} iconOnly ariaLabel="Close modal" label="Close" touch={isMobile} />
            )}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: useStickyFooter ? '1 1 0%' : undefined,
            // minHeight: 0 is critical for flex children that need to scroll.
            // Without it, a flex child defaults to minHeight: auto (content size),
            // which prevents it from shrinking below its content height and thus
            // prevents overflow scrolling from activating.
            minHeight: useStickyFooter ? 0 : undefined,
            overflowY: useStickyFooter ? 'auto' : undefined,
            padding: useStickyFooter ? `${theme.spacing[3]}px ${theme.spacing[4]}px 0px ${theme.spacing[3]}px` : undefined,
            marginBottom: footer && !useStickyFooter ? theme.spacing.component.modal.bodyGap : 0,
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            overscrollBehavior: useStickyFooter ? 'contain' : undefined, // Prevent scroll chaining to parent on iOS
          }}
        >
          {/* A modal is its own surface, so tab depth RESTARTS here. Depth is
              derived from React context, and context crosses a portal — so
              without this a dialog opened from inside two panels (Account →
              People → this) rendered its own primary tab row with the
              third-level leaf shape, styled as though it were nested under
              rows that are not even on screen. */}
          {/* Likewise the settings pages' segmented tab look (TabsKeepLabels) stays
              on the page: a dialog opened from a settings section keeps its own. */}
          <TabsKeepLabels value={false}>
            <TabsLevel value={0}>{children}</TabsLevel>
          </TabsKeepLabels>
        </div>

        {/* Footer - Sticky on mobile */}
        {footer && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.spacing.component.modal.footerGap,
              padding: useStickyFooter ? `${theme.spacing[3]}px ${theme.spacing[4]}px` : undefined,
              paddingTop: useStickyFooter ? theme.spacing[3] : theme.spacing.component.modal.bodyGap,
              paddingBottom: useStickyFooter
                ? isMobile
                  ? `max(${theme.spacing[4]}px, env(safe-area-inset-bottom))`
                  : theme.spacing[4]
                : undefined,
              borderTop: `1px solid ${tokenVars.borderSubtle}`,
              background: useStickyFooter ? tokenVars.surface : undefined,
              flexShrink: 0,
              position: useStickyFooter ? 'sticky' : undefined,
              bottom: useStickyFooter ? 0 : undefined,
              zIndex: useStickyFooter ? 10 : undefined,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
